export const ACTIVE_WS_KEY = "adjudon_active_workspace";

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Briefcase, CheckCircle2, Plus, X, Pencil, Trash2, Check, Loader2 } from "lucide-react";
import { useWorkspaces, useOrganization, useCreateWorkspace, useUpdateWorkspace, useDeleteWorkspace } from "../../hooks/useAPI";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "./PageHeader";
import { WidgetPlaceholder } from "./WidgetPlaceholder";
import { ErrorState } from "./ErrorState";
import { ConfirmDialog } from "./ConfirmDialog";
import { Tooltip } from "./ui/tooltip";
import { toast } from "sonner";

const STALE_AFTER_MS = 5 * 60 * 1000;
const WS_NAME_MAX_LENGTH = 80;

function resolveLocale(): string {
  if (typeof navigator === "undefined") return "en-GB";
  const langs = Array.isArray(navigator.languages) ? navigator.languages : null;
  if (langs && langs.length > 0 && typeof langs[0] === "string" && langs[0].length > 0) {
    return langs[0];
  }
  const lang = navigator.language;
  return typeof lang === "string" && lang.length > 0 ? lang : "en-GB";
}

function formatDate(value: string | undefined | null, locale: string): string {
  if (typeof value !== "string" || value.length === 0) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return d.toLocaleDateString(locale, { dateStyle: "medium" });
  } catch {
    return "—";
  }
}

const ICON_BUTTON_CLASS =
  "p-1.5 min-h-[32px] min-w-[32px] inline-flex items-center justify-center hover:bg-surface-1 border border-transparent hover:border-border transition-colors focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed";

// Backend-returned shape for /api/workspaces. Pre-R3 the dashboard read
// description/agentCount/memberCount/userRole, none of which the backend
// returned — see workspace_analyse_sprint_v1 memory for the rewire.
// memberCount + userRole remain absent because the schema has no per-
// workspace membership model (members are at the org level).
interface WorkspaceShape {
  _id: string;
  name?: string;
  createdAt?: string;
  agentCount?: number;
  apiKeysCount?: number;
}

export function WorkspaceDashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = useMemo(() => resolveLocale(), []);
  const { data: workspaces = [], isLoading, error, refetch, isFetching, dataUpdatedAt } = useWorkspaces();
  const { data: activeOrg } = useOrganization();
  const createWorkspaceMutation = useCreateWorkspace();
  const updateWorkspaceMutation = useUpdateWorkspace();
  const deleteWorkspaceMutation = useDeleteWorkspace();

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [renamingWsId, setRenamingWsId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<WorkspaceShape | null>(null);
  const [deletingWsId, setDeletingWsId] = useState<string | null>(null);
  const [updatingWsId, setUpdatingWsId] = useState<string | null>(null);
  const deleteTargetRef = useRef<WorkspaceShape | null>(null);
  useEffect(() => { deleteTargetRef.current = deleteTarget; }, [deleteTarget]);

  // Cardinal Rule #4: workspaces narrowed to WorkspaceShape[] without
  // `(as any[])` casts. useWorkspaces is typed but loose — local
  // narrowing keeps inline references type-safe.
  const wsList: WorkspaceShape[] = (workspaces ?? []) as WorkspaceShape[];
  const storedWsId = localStorage.getItem(ACTIVE_WS_KEY);
  const activeWsId = storedWsId && wsList.find((w) => w._id === storedWsId)
    ? storedWsId
    : wsList[0]?._id;

  const handleSwitch = (wsId: string, wsName: string) => {
    localStorage.setItem(ACTIVE_WS_KEY, wsId);
    queryClient.invalidateQueries();
    toast.success("Workspace switched", { description: wsName });
    navigate("/dashboard");
  };

  const handleCreate = () => {
    if (!newName.trim()) return;
    createWorkspaceMutation.mutate({ name: newName }, {
      onSuccess: (data) => {
        const newWs = data as { _id?: string } | undefined;
        if (newWs?._id) localStorage.setItem(ACTIVE_WS_KEY, newWs._id);
        queryClient.invalidateQueries();
        toast.success("Workspace created", { description: newName });
        navigate("/dashboard");
      },
      // Cardinal Rule #4: don't surface raw err.message — could leak.
      onError: () => toast.error("Failed to create workspace"),
    });
  };

  const startRename = (ws: WorkspaceShape) => {
    setRenamingWsId(ws._id);
    setRenameValue(ws.name ?? "");
  };

  const submitRename = async (ws: WorkspaceShape) => {
    const trimmed = renameValue.trim();
    if (!trimmed || updatingWsId) return;
    if (trimmed === (ws.name ?? "")) {
      setRenamingWsId(null);
      return;
    }
    setUpdatingWsId(ws._id);
    try {
      await updateWorkspaceMutation.mutateAsync({ id: ws._id, data: { name: trimmed } });
      toast.success("Workspace renamed", { description: trimmed });
      setRenamingWsId(null);
    } catch {
      // Cardinal Rule #4: don't surface raw err.message — name is OK.
      toast.error("Failed to rename workspace", { description: ws.name });
    } finally {
      setUpdatingWsId((prev) => (prev === ws._id ? null : prev));
    }
  };

  const handleDelete = (ws: WorkspaceShape) => {
    setDeleteTarget(ws);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleteWorkspaceMutation.isPending) return;
    const target = deleteTarget;
    setDeletingWsId(target._id);
    try {
      await deleteWorkspaceMutation.mutateAsync(target._id);
      toast.success("Workspace deleted", { description: target.name });
      // If we just deleted the active workspace, switch to the first remaining
      // one (if any). Falls through to dashboard which redirects when no
      // workspace exists.
      if (storedWsId === target._id) {
        const remaining = wsList.filter((w) => w._id !== target._id);
        if (remaining[0]?._id) {
          localStorage.setItem(ACTIVE_WS_KEY, remaining[0]._id);
        } else {
          localStorage.removeItem(ACTIVE_WS_KEY);
        }
      }
      queryClient.invalidateQueries();
      if (deleteTargetRef.current?._id === target._id) {
        setDeleteTarget(null);
      }
    } catch {
      // Cardinal Rule #4: don't surface raw err.message.
      toast.error("Failed to delete workspace", { description: target.name });
    } finally {
      setDeletingWsId((prev) => (prev === target._id ? null : prev));
    }
  };

  const header = (
    <PageHeader
      live
      title="Workspaces"
      subtitle={activeOrg?.name ?? "Organization"}
      updatedAt={dataUpdatedAt || undefined}
      isFetching={isFetching}
      staleAfterMs={STALE_AFTER_MS}
      recordCount={wsList.length}
      recordLabel={wsList.length === 1 ? "workspace" : "workspaces"}
      actions={
        <button
          type="button"
          onClick={() => setShowCreate(!showCreate)}
          data-testid="workspaces-create-toggle"
          aria-expanded={showCreate}
          className="adp-btn adp-btn-primary min-h-[32px] px-3 py-1.5 inline-flex items-center gap-1.5 text-xs font-semibold disabled:opacity-50 transition-colors motion-reduce:transition-none"
        >
          <Plus className="w-3.5 h-3.5" aria-hidden="true" />
          New Workspace
        </button>
      }
    />
  );

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="workspaces-loading">
        {header}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <WidgetPlaceholder key={i} title="KPI" height={104} />
          ))}
        </div>
        <WidgetPlaceholder title="Workspaces" height={400} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6" data-testid="workspaces-error">
        {header}
        <ErrorState
          code="WORKSPACES LOAD ERROR"
          title="Failed to load workspaces"
          description="Could not fetch your workspace list. Check your connection and retry."
          error={error instanceof Error ? error : null}
          retry={() => refetch()}
        />
      </div>
    );
  }

  // Derived stats — totalAgents now real (R2 backend aggregates),
  // totalApiKeys replaces the dead `Total Members` KPI (memberCount
  // had no schema source so it permanently rendered 0).
  const totalAgents = wsList.reduce((s, w) => s + (w.agentCount ?? 0), 0);
  const totalApiKeys = wsList.reduce((s, w) => s + (w.apiKeysCount ?? 0), 0);
  const activeWs = wsList.find((w) => w._id === activeWsId);

  return (
    <div className="space-y-6" data-testid="workspaces">
      {header}

      {/* Stats row — staggered animation removed for SDS consistency
          (no other v3 page uses staggered fade-in). */}
      <div
        className="grid grid-cols-1 sm:grid-cols-3 gap-4"
        role="group"
        aria-label="Workspace KPIs"
      >
        {[
          { label: "Total Workspaces", value: wsList.length, testId: "workspaces-kpi-total" },
          { label: "Total Agents",     value: totalAgents,   testId: "workspaces-kpi-agents" },
          { label: "Total API Keys",   value: totalApiKeys,  testId: "workspaces-kpi-keys" },
        ].map(({ label, value, testId }) => (
          <div className="adp-card" key={label} data-testid={testId}>
            <div className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground mb-2">
              {label}
            </div>
            <div className="text-3xl font-bold text-mono text-foreground tabular-nums">
              {value.toLocaleString(locale)}
            </div>
            {label === "Total Workspaces" && activeWs && (
              <div className="text-[10px] text-mono uppercase tracking-wider mt-1 text-foreground">
                Active: {activeWs.name}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create form (expanded from button) */}
      {showCreate && (
        <div className="adp-card space-y-4" data-testid="workspaces-create-form">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-mono uppercase tracking-wider text-foreground">
              New Workspace
            </span>
            <button
              type="button"
              onClick={() => { setShowCreate(false); setNewName(""); }}
              aria-label="Cancel new workspace"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          </div>
          <div>
            <label
              htmlFor="workspace-create-name"
              className="block text-[10px] text-mono uppercase tracking-wider text-muted-foreground mb-2"
            >
              Workspace Name <span aria-hidden="true">*</span>
            </label>
            <input
              id="workspace-create-name"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder="e.g., Production, Staging, Marketing"
              className="adp-input w-full text-sm"
              maxLength={WS_NAME_MAX_LENGTH}
              autoFocus
              data-testid="workspaces-create-name-input"
            />
          </div>
          <div className="flex gap-2 max-w-sm">
            <button
              type="button"
              onClick={() => { setShowCreate(false); setNewName(""); }}
              className="adp-btn flex-1 min-h-[32px] px-3 py-1.5 inline-flex items-center justify-center gap-1.5 text-xs font-semibold disabled:opacity-50 transition-colors motion-reduce:transition-none"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={!newName.trim() || createWorkspaceMutation.isPending}
              data-testid="workspaces-create-submit"
              className="adp-btn adp-btn-primary flex-1 min-h-[32px] px-3 py-1.5 inline-flex items-center justify-center gap-1.5 text-xs font-semibold disabled:opacity-50 transition-colors motion-reduce:transition-none"
            >
              {createWorkspaceMutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* Workspace cards grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {(workspaces as WorkspaceShape[]).map((ws, i) => {
          const isActive = ws._id === activeWsId;
          const isRenaming = renamingWsId === ws._id;
          const isUpdatingThis = updatingWsId === ws._id;
          const isDeletingThis = deletingWsId === ws._id;
          const onlyOneWs = (workspaces as WorkspaceShape[]).length <= 1;
          // Block delete for the LAST remaining workspace — every org needs at
          // least one. Active workspace can be deleted; we switch to another
          // afterwards.
          const canDelete = !onlyOneWs && !isDeletingThis && !isRenaming;
          const deleteTooltip = onlyOneWs
            ? "Cannot delete the only workspace"
            : isDeletingThis
              ? "Deleting…"
              : "Delete workspace";
          return (
            <div
              key={ws._id}
              className="adp-card flex flex-col"
              style={{
                animationDelay: `${i * 75}ms`,
                ...(isActive ? { borderColor: 'rgba(var(--brand-rgb),0.3)', background: 'rgba(var(--brand-rgb),0.04)' } : {}),
              }}
            >
              {/* Card header */}
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div
                    className="w-10 h-10 flex items-center justify-center border flex-shrink-0"
                    style={isActive
                      ? { borderColor: 'rgba(var(--brand-rgb),0.4)', background: 'rgba(var(--brand-rgb),0.08)' }
                      : { borderColor: 'var(--border)', background: 'var(--surface-2)' }
                    }
                  >
                    <Briefcase
                      className={`w-4 h-4 ${isActive ? "text-[var(--brand)]" : "text-muted-foreground"}`}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    {isRenaming ? (
                      <form
                        onSubmit={(e) => { e.preventDefault(); void submitRename(ws); }}
                        className="flex items-center gap-1"
                      >
                        <input
                          autoFocus
                          aria-label="Workspace name"
                          maxLength={WS_NAME_MAX_LENGTH}
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") { e.preventDefault(); setRenamingWsId(null); }
                          }}
                          data-testid={`workspaces-rename-input-${ws._id}`}
                          className="adp-input flex-1 min-w-0 px-2 py-1 min-h-[32px] text-sm"
                        />
                        <button
                          type="submit"
                          disabled={isUpdatingThis || !renameValue.trim()}
                          aria-label={isUpdatingThis ? `Saving name for ${ws.name}` : `Save name for ${ws.name}`}
                          aria-busy={isUpdatingThis}
                          data-testid={`workspaces-rename-submit-${ws._id}`}
                          className={`${ICON_BUTTON_CLASS} text-confidence`}
                        >
                          {isUpdatingThis ? <Loader2 className="w-3.5 h-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Check className="w-3.5 h-3.5" aria-hidden="true" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => setRenamingWsId(null)}
                          aria-label={`Cancel renaming ${ws.name}`}
                          className={`${ICON_BUTTON_CLASS} text-muted-foreground`}
                        >
                          <X className="w-3.5 h-3.5" aria-hidden="true" />
                        </button>
                      </form>
                    ) : (
                      <>
                        <h3 className="text-sm font-semibold text-foreground truncate">{ws.name}</h3>
                        <p className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground mt-0.5">
                          Created {formatDate(ws.createdAt, locale)}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isActive && !isRenaming && (
                    <span className="adp-badge badge-info">Active</span>
                  )}
                  {!isRenaming && (
                    <>
                      <Tooltip content="Rename workspace" position="top">
                        <button
                          type="button"
                          onClick={() => startRename(ws)}
                          aria-label={`Rename workspace ${ws.name}`}
                          disabled={isDeletingThis}
                          className={ICON_BUTTON_CLASS}
                        >
                          <Pencil className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
                        </button>
                      </Tooltip>
                      <Tooltip content={deleteTooltip} position="top">
                        <button
                          type="button"
                          onClick={() => handleDelete(ws)}
                          aria-label={isDeletingThis ? `Deleting workspace ${ws.name}` : `Delete workspace ${ws.name}`}
                          aria-busy={isDeletingThis}
                          disabled={!canDelete}
                          className={ICON_BUTTON_CLASS}
                        >
                          {isDeletingThis ? (
                            <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin motion-reduce:animate-none" aria-hidden="true" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
                          )}
                        </button>
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>

              {/* Stats — `Members` and `Your Role` removed in R3: the
                  schema has no per-workspace membership / RBAC, so those
                  two fields permanently rendered "—" / "Owner". The
                  rewired `API Keys` count IS workspace-scoped. */}
              <div
                className="grid grid-cols-2 border-t pt-3 mb-4"
                style={{ borderColor: 'var(--border-subtle)' }}
                data-testid={`workspace-stats-${ws._id}`}
              >
                {[
                  { label: 'Agents',   value: ws.agentCount   ?? 0 },
                  { label: 'API Keys', value: ws.apiKeysCount ?? 0 },
                ].map(({ label, value }) => (
                  <div key={label} className="text-center">
                    <div className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground mb-1">
                      {label}
                    </div>
                    <div className="text-sm font-bold text-mono text-foreground tabular-nums">
                      {typeof value === 'number' ? value.toLocaleString(locale) : value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Action */}
              <div className="mt-auto pt-3 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                {isActive ? (
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[var(--brand)]" aria-hidden="true" />
                    <span className="text-[11px] text-mono uppercase tracking-wider text-[var(--brand)]">
                      Currently Active
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSwitch(ws._id, ws.name ?? "")}
                    data-testid={`workspaces-switch-${ws._id}`}
                    className="adp-btn w-full text-[11px] text-mono uppercase tracking-wider py-2"
                  >
                    Switch to this workspace
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Add workspace card */}
        {!showCreate && (
          <button
            onClick={() => setShowCreate(true)}
            className="adp-card flex flex-col items-center justify-center gap-3 min-h-[200px] transition-colors motion-reduce:transition-none hover:border-foreground group"
            style={{
              borderStyle: 'dashed',
              animationDelay: `${wsList.length * 75}ms`,
            }}
          >
            <div
              className="w-10 h-10 flex items-center justify-center border group-hover:border-foreground transition-colors motion-reduce:transition-none"
              style={{ borderStyle: 'dashed', borderColor: 'var(--border)' }}
            >
              <Plus className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </div>
            <div className="text-center">
              <div className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                New Workspace
              </div>
              <div className="text-xs text-muted-foreground/60 mt-0.5">
                Create a new isolated environment
              </div>
            </div>
          </button>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => {
          if (deleteWorkspaceMutation.isPending) return;
          setDeleteTarget(null);
        }}
        onConfirm={() => { void confirmDelete(); }}
        title="Delete workspace"
        description={`Permanently delete "${deleteTarget?.name ?? ""}"? Agents and traces in this workspace will be detached. This cannot be undone.`}
        confirmText={deleteWorkspaceMutation.isPending ? "Deleting…" : "Delete workspace"}
        variant="danger"
      />
    </div>
  );
}
